import './ReportViewer.scss'
import { Machine } from '../../utils/models/Machine';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, Table } from 'react-bootstrap';
import CharD3, { charData } from '../CharProductionComponent/charD3.component';
import { getRandomNumber } from 'aw-generic-functions';
import { GraphLine, ICharData, ICharOptions } from 'aw-graph';
import { getGraphData } from '../../utils/services/data.service';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { toPng } from 'html-to-image';

type AlarmType = 'Minor' | 'Warning' | 'Critical' | 'No Alarms';

interface AlarmData {
  name: AlarmType;
  value: number;
}

const ReportViewer = (props: {
  allMachinesChecked: boolean,
  machine: Machine,
  refresh: boolean,
  token: string,
  year: number,
  month: number,
  alarmsForDay: (s: string) => void,
  itemsForDay: (s: string) => void,
  profitsForDay: (s: string) => void,
  errorsMonth: number,
  errorsTotal: number,
  productsMonth: number,
  productsTotal: number,
  productivityMonth: number,
  productivityTotal: number,
  user: any,
  captureAlarms?: boolean,
  onItemsPerDayData?: (data: { date: string; numItems: number }[]) => void
}) => {

  const getCharData = (): charData[] => {
    const charData: charData[] = []
    for (let i = 0; i < 30; i++) {
      charData.push({ date: `${i + 1}/${new Date().getMonth() + 1}`, numItems: getRandomNumber(0, 100) })
    }
    return charData
  }

  const [showData, setShowData] = useState(false)
  const { t } = useTranslation()
  const [charData, setCharData] = useState<ICharData[]>([]);
  const [alarmData, setAlarmData] = useState<AlarmData[]>([]);
  const [charD3Data, setCharD3Data] = useState<charData[]>([]);
  const [captureAlarmChart, setCaptureAlarmChart] = useState(false);
  const pieChartRef = useRef<HTMLDivElement>(null);

  const ALARM_COLORS: Record<AlarmType, string> = {
    'Minor': '#FBDB59',
    'Warning': '#EAA537',
    'Critical': '#CF594D',
    'No Alarms': '#A8AAA8'
  };

  const allCharOptions: ICharOptions[] = [
    { label: t('Profit'), key: 'Mesure 1', color: '#82ca9d', units: "€", minValue: 0, maxValue: 1400, value: 0 },
    { label: t('Losses'), key: 'Mesure 2', color: '#CF594D', units: "€", minValue: 0, maxValue: 1050, value: 1 },
  ];
  const [selectedCharOptions, setSelectedCharOptions] = useState<ICharOptions[]>(allCharOptions.slice(0, 2))

  useEffect(() => {
    let alarms: AlarmData[] = []
    if (props.allMachinesChecked) {
      alarms = [
        { name: 'Minor', value: getRandomNumber(15, 30) },
        { name: 'Warning', value: getRandomNumber(7, 20) },
        { name: 'Critical', value: getRandomNumber(3, 10) },
        { name: 'No Alarms', value: getRandomNumber(22, 45) }
      ];
    }
    else {
      alarms = [
        { name: 'Minor', value: getRandomNumber(5, 10) },
        { name: 'Warning', value: getRandomNumber(3, 8) },
        { name: 'Critical', value: getRandomNumber(1, 5) },
        { name: 'No Alarms', value: getRandomNumber(10, 15) }
      ];
    }
    setAlarmData(alarms);
  }, []);

  useEffect(() => {
    if (showData && captureAlarmChart && alarmData.length > 0 && props.alarmsForDay) {
      setTimeout(() => {
        if (pieChartRef.current) {
          toPng(pieChartRef.current)
            .then((dataUrl) => {
              props.alarmsForDay(dataUrl);
              setCaptureAlarmChart(false);
            })
            .catch((e) => {
              console.error('Error capture Alarm Image', e);
              setCaptureAlarmChart(false);
            });
        } else {
          console.log("pieChartRef.current is null after timeout");
          setCaptureAlarmChart(false);
        }
      }, 1000);
    }
  }, [showData, captureAlarmChart, alarmData.length, props.alarmsForDay]);

  useEffect(() => {
    if (props.refresh) {
      setTimeout(() => setShowData(true), 1000)
    }
  }, [props.refresh])

  useEffect(() => {
    const monthStr = props.month.toString().padStart(2, '0');
    const startDate = `${props.year}-${monthStr}-01`;
    const endDate = `${props.year}-${monthStr}-30`;

    const d3 = getCharData();
    setCharD3Data(d3);
    props.onItemsPerDayData?.(d3);

    getGraphData(props.user.token, startDate, endDate, JSON.stringify(allCharOptions))
      .then((data: ICharData[]) => {
        setCharData(data);
      })
      .catch((e) => {
        console.error('Error getElectricalDataChar', e);
      })
      .finally(() => {
        setTimeout(() => {
        }, 250);
      });
  }, []); 

  const generateReportClick = () => {
    setShowData(false);
    setTimeout(() => {
      setCaptureAlarmChart(true);
      setShowData(true)
    }, 250)
  }

  if (!showData) {
    return (
      <>
        <div className='flex text-center align-items-center no-report-container p-3 rounded-1 w-50' style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)' }}>
          <button className='btn-report-viewer mt-2' onClick={() => generateReportClick()}><i className='bi-bibi bi-file-earmark-bar-graph me-2'></i>{`${t('GenerateReport')}...`}</button>
        </div>
      </>
    )
  }

  if (props.refresh) {
    return <div className="text-center pt-5"><Spinner animation="border" variant="success" /></div>
  }

  return (
    <div className="container-fluid p-2">
      <div className="row border-0">
        <div className="col-lg-8 col-md-12 mt-1 px-1 ">
          <CharD3
            dataChar={charD3Data}
            loading={props.refresh}
            capture={true}
            title={t('ItemsForDay')}
            setPNGChar={props.itemsForDay}
          />
        </div>

        <div className="col-lg-4 col-md-12 px-1 mt-1">
          <div className="gridTotal rounded-1 report-container"  >
            <div className='row'>
              <span className='text-center h4 mt-2'> {t('GlobalIndicators')}</span>
            </div>
            <div className="table-responsive scrollable-GridTotal mt-2">
              <Table hover className='gridsStyle'>
                <thead className="sticky-top">
                  <tr>
                    <th style={{ width: '40%' }}></th>
                    <th>{t('Monthly')}</th>
                    <th>{t('Total')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th className='text-start'>{t('Errors')}</th>
                    <td>{props.errorsMonth}</td>
                    <td>{props.errorsTotal}</td>
                  </tr>
                  <tr>
                    <th className='text-start'>{t('Items')}</th>
                    <td>{props.productsMonth}</td>
                    <td>{props.productsTotal}</td>
                  </tr>
                  <tr>
                    <th className='text-start'>{t('Productivity')}</th>
                    <td>{`${props.productivityMonth} %`}</td>
                    <td>{`${props.productivityTotal} %`}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      <div className='row border-0 mt-1'>
        <div className="col-lg-8 col-md-12 px-1 mt-1 " style={{ maxWidth: '100%' }}>
          <GraphLine
            refresh={false}
            showDots={false}
            showSelect={false}
            options={allCharOptions}
            prevSelectedOptions={selectedCharOptions}
            data={charData}
            onChangeSelectedOptions={setSelectedCharOptions}
            lineChartMarginLeft={0}
            lineChartMarginRight={0}
            spinnerColor="var(--IDGreen)"
            backgroundColor="var(--background-secondary)"
            textColor="var(--text-primary)"
            containerTitle={t('Profits')}
            capture={true}
            setPNGChar={props.profitsForDay}
          />
        </div>
        <div className="col-lg-4 col-md-12 px-1 mt-1">
          <div className='report-container rounded-1' >
            <div className='row'>
              <span className='text-center h4 mt-3'> {t('Alarm Distribution')}</span>
            </div>
            <ResponsiveContainer width="100%" height={320} ref={pieChartRef}>
              <PieChart>
                <Pie
                  data={alarmData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius="80%"
                  innerRadius="0%"
                  fill="#8884d8"
                  dataKey="value"
                >
                  {alarmData.map((entry) => (
                    <Cell
                      key={`cell-${entry.name}`}
                      fill={ALARM_COLORS[entry.name]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value}`, `${name} Alarms`]} />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;
